import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@nsibi/ui-web';

import {
  HandshakeExpiredError,
  openHandshake,
  waitForApproval,
  type LinkHandshake,
} from '../lib/link';
import type { Session } from '../lib/session';
import { appLinkFor } from '../lib/phoneBrowser';
import { Trans, useTranslation } from "react-i18next";

type State =
  | { kind: 'opening' }
  | { kind: 'waiting'; handshake: LinkHandshake; svg: string }
  | { kind: 'expired' }
  | { kind: 'error'; message: string }
  | { kind: 'approved' };

export function QrSignIn({
  onSignedIn,
  title,
  phone = false,
}: {
  onSignedIn: (s: Session) => void;
  /** The heading; the card hand-off says what the sign-in is for. */
  title?: string;
  /** A phone with the app on it: lead with "Open Nsibi", no code to scan.
   *  The page decides (it also offers "Show QR code" instead). */
  phone?: boolean;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'opening' });
  const [attempt, setAttempt] = useState(0);
  const heading = title ?? t("components.signInToNsibi");

  const refresh = useCallback(() => {
    setState({ kind: 'opening' });
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        const handshake = await openHandshake(ac.signal);

        // SVG rather than a canvas so it stays crisp at any size and takes
        // the page's ink colour instead of baking in black.
        const svg = await QRCode.toString(handshake.qrPayload, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 2,
          color: { dark: '#14120f', light: '#00000000' },
        });
        if (ac.signal.aborted) return;
        setState({ kind: 'waiting', handshake, svg });

        const session = await waitForApproval(handshake, ac.signal);
        if (ac.signal.aborted) return;
        setState({ kind: 'approved' });
        onSignedIn(session);
      } catch (err) {
        if (ac.signal.aborted || (err as Error)?.name === 'AbortError') return;
        if (err instanceof HandshakeExpiredError) setState({ kind: 'expired' });
        else
          setState({
            kind: 'error',
            message: (err as Error)?.message ?? t("common.genericErrorDot"),
          });
      }
    })();

    return () => ac.abort();
  }, [attempt, onSignedIn]);

  const status =
    state.kind === 'approved' ? (
      <>
        <span className="size-1.5 rounded-full bg-palm" />
        {t("components.signingYouIn")}
      </>
    ) : state.kind === 'waiting' ? (
      <>
        <span className="size-1.5 animate-pulse rounded-full bg-palm" />
        {t("components.waitingForYourPhone")}
      </>
    ) : null;

  // On a phone the app is usually on this same device: there is nothing to
  // scan with, so the page leads with a link that opens the app on this
  // code (`nsibi://link-device?p=`, the QR's own payload).
  if (phone) {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <h1 className="text-display">{heading}</h1>
        {state.kind === 'waiting' || state.kind === 'approved' ? (
          <Button asChild size="lg" className="w-full">
            <a href={state.kind === 'waiting' ? appLinkFor(state.handshake.qrPayload) : undefined}>
              {t('components.openNsibiToSignIn', { defaultValue: 'Open Nsibi to sign in' })}
            </a>
          </Button>
        ) : state.kind === 'opening' ? (
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {state.kind === 'expired' ? t("components.thisCodeExpired") : state.message}
            </p>
            <Button size="lg" variant="outline" className="w-full" onClick={refresh}>
              <RefreshCw />
              {t("components.newCode")}
            </Button>
          </>
        )}
        <p className="flex items-center gap-2 text-xs text-muted-foreground">{status}</p>
      </div>
    );
  }

  return (
    <div className="grid items-center gap-8 sm:grid-cols-[18rem_minmax(0,1fr)] sm:gap-12">
      {/* code */}
      <div className="mx-auto flex size-[18rem] max-w-full flex-none items-center justify-center rounded-2xl border border-border bg-card p-5 shadow-card sm:mx-0">
        {state.kind === 'waiting' && (
          <div
            className="size-full [&>svg]:size-full"
            // QRCode.toString returns a self-contained SVG document, built
            // from a link id and a device id this app just generated. No
            // untrusted content passes through here.
            dangerouslySetInnerHTML={{ __html: state.svg }}
          />
        )}

        {state.kind === 'opening' && (
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        )}

        {(state.kind === 'expired' || state.kind === 'error') && (
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="max-w-[13rem] text-sm leading-snug text-muted-foreground">
              {state.kind === 'expired' ? t("components.thisCodeExpired") : state.message}
            </p>
            <Button size="sm" variant="outline" onClick={refresh}>
              <RefreshCw />
              {t("components.newCode")}
            </Button>
          </div>
        )}

        {state.kind === 'approved' && (
          <Loader2 className="size-6 animate-spin text-palm" />
        )}
      </div>

      {/* instructions */}
      <div className="text-center sm:text-left">
        <h1 className="text-display">{heading}</h1>
        <ol className="mt-5 flex flex-col gap-2.5 text-sm text-muted-foreground">
          <Step n={1}>{t("components.openNsibiOnYourPhone")}</Step>
          <Step n={2}>
            <Trans
              i18nKey="components.tapSettingsThenLinkedDevices"
              components={{
                settings: <span className="text-foreground" />,
                devices: <span className="text-foreground" />,
              }}
            />
          </Step>
          <Step n={3}>{t("components.pointYourPhoneAtThis")}</Step>
        </ol>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground sm:justify-start">
          {status}
        </p>
      </div>
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3 text-left">
      <span className="mt-0.5 flex size-5 flex-none items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}
